import axios, { AxiosResponse, AxiosError } from 'axios';
import type { 
  LoginCredentials, 
  RegisterData, 
  VehicleFormData, 
  MaintenanceFormData,
  InterventionFormData,
  EquipmentFormData,
  EquipmentUsageFormData,
  ParkingSpotFormData,
  Vehicle,
  SubClient,
  SubClientGroup,
  VehicleType,
  VehicleCategory,
  Client,
  MaintenanceRecord,
  Intervention,
  Equipment,
  EquipmentUsage,
  ParkingSpot,
  Alert,
  Communication,
  ApiError,
  EquipmentStats,
  MaintenanceStats,
  PaginatedResponse,
  VehicleFilters,
  MaintenanceFilters,
  AlertFilters,
  EquipmentFilters,
  VehicleConditionAssessment,
  VehicleConditionAssessmentFormData,
  VehicleConditionAssessmentResponse,
  VehicleConditionAssessmentListResponse,
  VehicleConditionAssessmentHistoryResponse,
  VehicleConditionAssessmentAfterScanResponse,
  VehicleConditionAssessmentSuggestAfterRepairResponse,
  PricingPlan,
  PricingTier,
  PricingPack,
  SeasonalRate,
  Prestation,
  PrestationTierRate,
  CategoryPrestationRate,
  TypePrestationRate,
  User,
  Pays,
  Agency,
  Reservation,
  ReservationAvailabilityParams,
  ReservationAvailabilityResponse,
} from '../types';
import type { ParkingLayout, ParkingElement, ParkingPreset } from '../types/parking';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 second timeout (in ms)
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // If sending FormData, don't set Content-Type header (let browser set it with boundary)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<{ token: string; user: any }>> =>
    api.post('/auth/login', credentials),
  register: (userData: RegisterData): Promise<AxiosResponse<{ token: string; user: any }>> => 
    api.post('/auth/register', userData),
  logout: (): Promise<AxiosResponse> => api.post('/auth/logout'),
  getProfile: (): Promise<AxiosResponse<any>> => api.get('/auth/user'),
};

// Vehicle service
export const vehicleService = {
  getAll: (params?: VehicleFilters): Promise<AxiosResponse<PaginatedResponse<Vehicle>>> => 
    api.get('/vehicles', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: Vehicle }>> => 
    api.get(`/vehicles/${id}`),
  create: (data: VehicleFormData): Promise<AxiosResponse<{ data: Vehicle; message: string }>> => {
    // Check if data contains files (FormData needed)
    const hasFiles = !!(data.primary_image instanceof File || (data.additional_images && data.additional_images.length > 0 && data.additional_images.some(img => img instanceof File)));
    
    if (hasFiles) {
      const formData = new FormData();
      
      // Add all non-file fields
      Object.keys(data).forEach(key => {
        if (key === 'primary_image' || key === 'additional_images') {
          return; // Handle files separately
        }
        
        if (data[key as keyof VehicleFormData] !== null && data[key as keyof VehicleFormData] !== undefined) {
          const value = data[key as keyof VehicleFormData];
          // Convert boolean values to proper boolean strings
          if (typeof value === 'boolean') {
            formData.append(key, value ? '1' : '0');
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Add primary image if present
      if (data.primary_image) {
        formData.append('primary_image', data.primary_image);
      }
      
      // Add additional images if present
      if (data.additional_images && data.additional_images.length > 0) {
        data.additional_images.forEach((file: File, index: number) => {
          formData.append(`additional_images[${index}]`, file);
        });
      }
      
      return api.post('/vehicles', formData, {
        headers: {
          // Let the browser set the correct Content-Type with boundary for FormData
        },
      });
    }
    
    return api.post('/vehicles', data);
  },
  update: (id: number, data: Partial<VehicleFormData>): Promise<AxiosResponse<{ data: Vehicle; message: string }>> => {
    // Check if data contains files (FormData needed)
    const hasFiles = !!(data.primary_image instanceof File || (data.additional_images && data.additional_images.length > 0 && data.additional_images.some(img => img instanceof File)));
    
    // Debug: Log what's being sent
    console.log('Vehicle update data:', {
      hasFiles,
      primary_image: data.primary_image,
      primary_image_type: data.primary_image?.constructor?.name,
      additional_images: data.additional_images,
      additional_images_types: data.additional_images?.map(img => img?.constructor?.name),
      remove_primary_image: data.remove_primary_image,
      remove_additional_images: data.remove_additional_images,
      allKeys: Object.keys(data)
    });
    
    if (hasFiles) {
      const formData = new FormData();
      
      console.log('Creating FormData with files...');
      
      // Add all non-file fields
      Object.keys(data).forEach(key => {
        if (key === 'primary_image' || key === 'additional_images') {
          return; // Handle files separately
        }
        
        if (key === 'remove_additional_images' && Array.isArray(data[key as keyof VehicleFormData])) {
          (data[key as keyof VehicleFormData] as string[])?.forEach((filename: string, index: number) => {
            formData.append(`remove_additional_images[${index}]`, filename);
            console.log(`Added remove_additional_images[${index}]:`, filename);
          });
        } else if (data[key as keyof VehicleFormData] !== null && data[key as keyof VehicleFormData] !== undefined) {
          const value = data[key as keyof VehicleFormData];
          // Convert boolean values to proper boolean strings
          if (typeof value === 'boolean') {
            formData.append(key, value ? '1' : '0');
            console.log(`Added field ${key}:`, value ? '1' : '0');
          } else {
            formData.append(key, String(value));
            console.log(`Added field ${key}:`, String(value));
          }
        }
      });
      
      // Add primary image if present
      if (data.primary_image) {
        formData.append('primary_image', data.primary_image);
        console.log('Added primary_image:', data.primary_image.name);
      }
      
      // Add additional images if present
      if (data.additional_images && data.additional_images.length > 0) {
        data.additional_images.forEach((file: File, index: number) => {
          formData.append(`additional_images[${index}]`, file);
          console.log(`Added additional_images[${index}]:`, file.name);
        });
      }
      
      // Debug: Log FormData contents
      console.log('FormData entries:');
      formData.forEach((value, key) => {
        console.log(`${key}:`, value);
      });
      
      return api.post(`/vehicles/${id}/update-with-files`, formData, {
        headers: {
          // Let the browser set the correct Content-Type with boundary for FormData
        },
      });
    }
    
    return api.put(`/vehicles/${id}`, data);
  },
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicles/${id}`),
  export: (params?: VehicleFilters): Promise<AxiosResponse<Blob>> =>
    api.post('/vehicles/export', params, { responseType: 'blob', timeout: 120000 }),
  getDocuments: (id: number): Promise<AxiosResponse<{ data: any[] }>> => 
    api.get(`/vehicles/${id}/documents`),
  uploadDocument: (id: number, formData: FormData): Promise<AxiosResponse<{ data: any; message: string }>> =>
    api.post(`/vehicles/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  uploadImages: (id: number, images: { primary_image?: File; additional_images?: File[] }): Promise<AxiosResponse<{ data: Vehicle; message: string }>> => {
    const formData = new FormData();
    if (images.primary_image) {
      formData.append('primary_image', images.primary_image);
    }
    if (images.additional_images) {
      images.additional_images.forEach((file, index) => {
        formData.append(`additional_images[${index}]`, file);
      });
    }
    return api.post(`/vehicles/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeImages: (id: number, removeData: { remove_primary_image?: boolean; remove_additional_images?: string[] }): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/vehicles/${id}/images`, { data: removeData }),
};

// Maintenance service
export const maintenanceService = {
  getAll: (params?: MaintenanceFilters): Promise<AxiosResponse<PaginatedResponse<MaintenanceRecord>>> => 
    api.get('/maintenance', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: MaintenanceRecord }>> => 
    api.get(`/maintenance/${id}`),
  create: (data: MaintenanceFormData): Promise<AxiosResponse<{ data: MaintenanceRecord; message: string }>> => 
    api.post('/maintenance', data),
  update: (id: number, data: Partial<MaintenanceFormData>): Promise<AxiosResponse<{ data: MaintenanceRecord; message: string }>> => 
    api.put(`/maintenance/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/maintenance/${id}`),
  updateStatus: (id: number, status: string): Promise<AxiosResponse<{ data: MaintenanceRecord; message: string }>> =>
    api.patch(`/maintenance/${id}/status`, { status }),
  getByVehicle: (vehicleId: number): Promise<AxiosResponse<{ data: MaintenanceRecord[] }>> => 
    api.get(`/vehicles/${vehicleId}/maintenance`),
  getStats: (): Promise<AxiosResponse<{ data: MaintenanceStats }>> => 
    api.get('/maintenance-stats'),
  bulkCreate: (data: { maintenance_records: MaintenanceFormData[] }): Promise<AxiosResponse<{ data: MaintenanceRecord[]; message: string }>> => 
    api.post('/maintenance/bulk-create', data),
  getCostAnalysis: (params?: { date_from?: string; date_to?: string }): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/maintenance/cost-analysis', { params }),
  generateSchedule: (vehicleId: number, data: { interval_km: number; start_date: string }): Promise<AxiosResponse<{ data: MaintenanceRecord[]; message: string }>> => 
    api.post(`/vehicles/${vehicleId}/generate-schedule`, data),
  startCalculatedMaintenance: (data: { vehicle_id: number; maintenance_type: string; description: string }): Promise<AxiosResponse<{ data: MaintenanceRecord; message: string }>> => 
    api.post('/maintenance/calculated/start', data),
  completeCalculatedMaintenance: (data: { vehicle_id: number; maintenance_type: string; description: string }): Promise<AxiosResponse<{ data: MaintenanceRecord; message: string }>> => 
    api.post('/maintenance/calculated/complete', data),
};

// Intervention service
export const interventionService = {
  getAll: (params?: any): Promise<AxiosResponse<PaginatedResponse<Intervention>>> => 
    api.get('/interventions', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: Intervention }>> => 
    api.get(`/interventions/${id}`),
  create: (data: InterventionFormData): Promise<AxiosResponse<{ data: Intervention; message: string }>> => 
    api.post('/interventions', data),
  update: (id: number, data: Partial<InterventionFormData>): Promise<AxiosResponse<{ data: Intervention; message: string }>> => 
    api.put(`/interventions/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/interventions/${id}`),
  updateStatus: (id: number, status: string): Promise<AxiosResponse<{ data: Intervention; message: string }>> =>
    api.patch(`/interventions/${id}/status`, { status }),
  getByVehicle: (vehicleId: number): Promise<AxiosResponse<{ data: Intervention[] }>> => 
    api.get(`/vehicles/${vehicleId}/interventions`),
  getTypes: (): Promise<AxiosResponse<{ data: Array<{value: string, label: string}> }>> => 
    api.get('/interventions/types'),
};

// Parking service
export const parkingService = {
  getAll: (): Promise<AxiosResponse<{ data: ParkingSpot[] }>> => 
    api.get('/parking-spots'),
  create: (data: ParkingSpotFormData): Promise<AxiosResponse<{ data: ParkingSpot; message: string }>> => 
    api.post('/parking-spots', data),
  update: (id: number, data: Partial<ParkingSpotFormData>): Promise<AxiosResponse<{ data: ParkingSpot; message: string }>> => 
    api.put(`/parking-spots/${id}`, data),
  assignVehicle: (spotId: number, vehicleId: number): Promise<AxiosResponse<{ data: ParkingSpot; message: string }>> =>
    api.patch(`/parking-spots/${spotId}/assign`, { vehicle_id: vehicleId }),
  releaseVehicle: (spotId: number): Promise<AxiosResponse<{ data: ParkingSpot; message: string }>> =>
    api.patch(`/parking-spots/${spotId}/release`),
  saveVisualLayout: (spots: any[]): Promise<AxiosResponse<{ data: ParkingSpot[]; message: string }>> =>
    api.post('/parking-spots/save-visual-layout', { spots }),
};

// Alert service
export const alertService = {
  getAll: (params?: AlertFilters): Promise<AxiosResponse<PaginatedResponse<Alert>>> => 
    api.get('/alerts', { params }),
  markAsRead: (id: number): Promise<AxiosResponse<{ data: Alert; message: string }>> => 
    api.patch(`/alerts/${id}/read`),
  markAsResolved: (id: number): Promise<AxiosResponse<{ data: Alert; message: string }>> => 
    api.patch(`/alerts/${id}/resolve`),
};

// Equipment service
export const equipmentService = {
  getAll: (params?: EquipmentFilters): Promise<AxiosResponse<PaginatedResponse<Equipment>>> => 
    api.get('/equipment', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: Equipment }>> => 
    api.get(`/equipment/${id}`),
  create: (data: EquipmentFormData): Promise<AxiosResponse<{ data: Equipment; message: string }>> => 
    api.post('/equipment', data),
  update: (id: number, data: Partial<EquipmentFormData>): Promise<AxiosResponse<{ data: Equipment; message: string }>> => 
    api.put(`/equipment/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/equipment/${id}`),
  getUsage: (params?: { equipment_id?: number; vehicle_id?: number; user_id?: number; status?: string; per_page?: number; page?: number }): Promise<AxiosResponse<{ data: EquipmentUsage[]; total?: number; current_page?: number; last_page?: number; per_page?: number }>> =>
    api.get('/equipment-usage', { params }),
  recordUsage: (data: EquipmentUsageFormData): Promise<AxiosResponse<{ data: EquipmentUsage; message: string }>> => 
    api.post('/equipment-usage', data),
  returnEquipment: (usageId: number, data: { returned_date: string; quantity_returned?: number }): Promise<AxiosResponse<{ data: EquipmentUsage; message: string }>> => 
    api.patch(`/equipment-usage/${usageId}/return`, data),
  getLowStock: (): Promise<AxiosResponse<{ data: Equipment[]; count: number }>> => 
    api.get('/equipment/low-stock'),
  getStats: (): Promise<AxiosResponse<{ data: EquipmentStats }>> => 
    api.get('/equipment/stats'),
  getUsageAnalytics: (params?: { start_date?: string; end_date?: string }): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/equipment/usage-analytics', { params }),
  bulkUpdateStock: (data: { updates: Array<{ equipment_id: number; quantity_adjustment: number; reason?: string }> }): Promise<AxiosResponse<{ data: any[]; message: string }>> => 
    api.post('/equipment/bulk-update-stock', data),
  generateStockReport: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/equipment/stock-report'),
  search: (params?: EquipmentFilters): Promise<AxiosResponse<{ data: Equipment[]; total: number; filters_applied: EquipmentFilters }>> => 
    api.get('/equipment/search', { params }),
};

// Communication service
export const communicationService = {
  getAll: (params?: { priority?: string; is_read?: boolean }): Promise<AxiosResponse<PaginatedResponse<Communication>>> => 
    api.get('/communications', { params }),
  create: (data: { subject: string; message: string; priority: string; to_department?: string; vehicle_id?: number; equipment_id?: number }): Promise<AxiosResponse<{ data: Communication; message: string }>> => 
    api.post('/communications', data),
  markAsRead: (id: number): Promise<AxiosResponse<{ data: Communication; message: string }>> => 
    api.patch(`/communications/${id}/read`),
};

// Parking Layout service
export const parkingLayoutService = {
  // Layouts
  getAll: (): Promise<AxiosResponse<{ data: ParkingLayout[] }>> => 
    api.get('/parking-layouts'),
  getById: (id: string): Promise<AxiosResponse<{ data: ParkingLayout }>> => 
    api.get(`/parking-layouts/${id}`),
  create: (data: Partial<ParkingLayout>): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post('/parking-layouts', data),
  update: (id: string, data: Partial<ParkingLayout>): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.put(`/parking-layouts/${id}`, data),
  delete: (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/parking-layouts/${id}`),
  
  // Presets and templates
  getPresets: (): Promise<AxiosResponse<{ data: ParkingPreset[] }>> => 
    api.get('/parking-layouts/presets'),
  getTemplates: (): Promise<AxiosResponse<{ data: ParkingLayout[] }>> => 
    api.get('/parking-layouts/templates'),
  
  // Layout operations
  publish: (id: string): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post(`/parking-layouts/${id}/publish`),
  archive: (id: string): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post(`/parking-layouts/${id}/archive`),
  duplicate: (id: string): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post(`/parking-layouts/${id}/duplicate`),
  toTemplate: (id: string): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post(`/parking-layouts/${id}/to-template`),
  
  // Layout data
  saveLayoutData: (id: string, data: { spots: any[]; elements: any[] }): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post(`/parking-layouts/${id}/save-layout-data`, data),
  
  // Export/Import
  export: (id: string): Promise<AxiosResponse<{ data: any }>> => 
    api.get(`/parking-layouts/${id}/export`),
  import: (data: any): Promise<AxiosResponse<{ data: ParkingLayout; message: string }>> => 
    api.post('/parking-layouts/import', data),
  
  // Statistics
  getStatistics: (id: string): Promise<AxiosResponse<{ data: any }>> => 
    api.get(`/parking-layouts/${id}/statistics`),
};

// Parking Element service
export const parkingElementService = {
  getAll: (layoutId: string): Promise<AxiosResponse<{ data: ParkingElement[] }>> => 
    api.get(`/parking-layouts/${layoutId}/elements`),
  getById: (layoutId: string, elementId: string): Promise<AxiosResponse<{ data: ParkingElement }>> => 
    api.get(`/parking-layouts/${layoutId}/elements/${elementId}`),
  create: (layoutId: string, data: Partial<ParkingElement>): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.post(`/parking-layouts/${layoutId}/elements`, data),
  update: (layoutId: string, elementId: string, data: Partial<ParkingElement>): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.put(`/parking-layouts/${layoutId}/elements/${elementId}`, data),
  delete: (layoutId: string, elementId: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/parking-layouts/${layoutId}/elements/${elementId}`),
  
  // Element operations
  duplicate: (layoutId: string, elementId: string): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.post(`/parking-layouts/${layoutId}/elements/${elementId}/duplicate`),
  move: (layoutId: string, elementId: string, data: { x: number; y: number }): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/move`, data),
  resize: (layoutId: string, elementId: string, data: { width: number; height: number }): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/resize`, data),
  rotate: (layoutId: string, elementId: string, data: { rotation: number }): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/rotate`, data),
  
  // Visibility and locking
  toggleVisibility: (layoutId: string, elementId: string): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/toggle-visibility`),
  toggleLock: (layoutId: string, elementId: string): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/toggle-lock`),
  changeZIndex: (layoutId: string, elementId: string, data: { z_index: number }): Promise<AxiosResponse<{ data: ParkingElement; message: string }>> => 
    api.patch(`/parking-layouts/${layoutId}/elements/${elementId}/change-z-index`, data),
  
  // Bulk operations
  bulkUpdate: (layoutId: string, data: { elements: Array<{ id: string; updates: Partial<ParkingElement> }> }): Promise<AxiosResponse<{ data: ParkingElement[]; message: string }>> => 
    api.post(`/parking-layouts/${layoutId}/elements/bulk-update`, data),
  bulkDelete: (layoutId: string, data: { element_ids: string[] }): Promise<AxiosResponse<{ message: string }>> => 
    api.post(`/parking-layouts/${layoutId}/elements/bulk-delete`, data),
};

// Vehicle Type service
export const vehicleTypeService = {
  getAll: (): Promise<AxiosResponse<{ data: VehicleType[]; total: number }>> => 
    api.get('/vehicle-types'),
  getById: (id: number): Promise<AxiosResponse<{ data: VehicleType }>> => 
    api.get(`/vehicle-types/${id}`),
  create: (data: Partial<VehicleType>): Promise<AxiosResponse<{ data: VehicleType; message: string }>> => 
    api.post('/vehicle-types', data),
  update: (id: number, data: Partial<VehicleType>): Promise<AxiosResponse<{ data: VehicleType; message: string }>> => 
    api.put(`/vehicle-types/${id}`, data),
  /** Update vehicle type with optional image (multipart). POST+_method=PUT so PHP receives the file. */
  updateWithImage: (id: number, formData: FormData): Promise<AxiosResponse<{ data: VehicleType; message: string }>> => {
    formData.append('_method', 'PUT');
    return api.post(`/vehicle-types/${id}`, formData);
  },
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicle-types/${id}`),
};

// Vehicle Category service
export const vehicleCategoryService = {
  getAll: (): Promise<AxiosResponse<{ data: VehicleCategory[]; total: number }>> => 
    api.get('/vehicle-categories'),
  getById: (id: number): Promise<AxiosResponse<{ data: VehicleCategory }>> => 
    api.get(`/vehicle-categories/${id}`),
  create: (data: Partial<VehicleCategory>): Promise<AxiosResponse<{ data: VehicleCategory; message: string }>> => 
    api.post('/vehicle-categories', data),
  update: (id: number, data: Partial<VehicleCategory>): Promise<AxiosResponse<{ data: VehicleCategory; message: string }>> => 
    api.put(`/vehicle-categories/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicle-categories/${id}`),
};

// Pays (countries) service - for select inputs
export const paysService = {
  getAll: (): Promise<AxiosResponse<{ data: Pays[] }>> =>
    api.get('/pays'),
};

// Client service
export const clientService = {
  getAll: (): Promise<AxiosResponse<{ data: Client[]; total: number }>> => 
    api.get('/clients'),
  getById: (id: number): Promise<AxiosResponse<{ data: Client }>> => 
    api.get(`/clients/${id}`),
  create: (data: Partial<Client>): Promise<AxiosResponse<{ data: Client; message: string }>> => 
    api.post('/clients', data),
  update: (id: number, data: Partial<Client>): Promise<AxiosResponse<{ data: Client; message: string }>> => 
    api.put(`/clients/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/clients/${id}`),
  createUser: (clientId: number, userData: any): Promise<AxiosResponse<{ data: any; message: string }>> => 
    api.post(`/clients/${clientId}/users`, userData),
  getUsers: (clientId: number): Promise<AxiosResponse<{ data: any[]; total: number }>> => 
    api.get(`/clients/${clientId}/users`),
  updateUser: (clientId: number, userId: number, userData: any): Promise<AxiosResponse<{ data: any; message: string }>> => 
    api.put(`/clients/${clientId}/users/${userId}`, userData),
  deleteUser: (clientId: number, userId: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/clients/${clientId}/users/${userId}`),
  uploadLogo: (clientId: number, logoFile: File): Promise<AxiosResponse<{ data: { logo_url: string; logo_path: string }; message: string }>> => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    return api.post(`/clients/${clientId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (clientId: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/clients/${clientId}/logo`),
};

// Profile service (for client users: own profile and users management)
export const profileService = {
  getProfile: (): Promise<AxiosResponse<{ data: Client }>> =>
    api.get('/profile'),
  updateProfile: (data: Partial<Pick<Client, 'name' | 'civilite' | 'date_naissance' | 'lieu_naissance' | 'email' | 'phone' | 'address' | 'accept_sms_email'>>): Promise<AxiosResponse<{ data: Client; message: string }>> =>
    api.put('/profile', data),
  getUsers: (): Promise<AxiosResponse<{ data: User[]; total: number }>> =>
    api.get('/profile/users'),
  createUser: (userData: { name: string; email: string; password: string; phone?: string; role_name: string }): Promise<AxiosResponse<{ data: User; message: string }>> =>
    api.post('/profile/users', userData),
  updateUser: (
    userId: number,
    userData: Partial<{
      name: string;
      email: string;
      password: string;
      phone: string;
      role_name: string;
      agency_id: number | null;
    }>
  ): Promise<AxiosResponse<{ data: User; message: string }>> =>
    api.put(`/profile/users/${userId}`, userData),
  deleteUser: (userId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/profile/users/${userId}`),
  uploadLogo: (logoFile: File): Promise<AxiosResponse<{ data: { logo_url: string; logo_path: string }; message: string }>> => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    return api.post('/profile/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (): Promise<AxiosResponse<{ message: string }>> =>
    api.delete('/profile/logo'),
};

export interface ExternalTechnician {
  id: number;
  client_id: number;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  specialty?: string;
  notes?: string;
  is_active: boolean;
}

export const externalTechnicianService = {
  getAll: (params?: { is_active?: boolean }): Promise<AxiosResponse<{ data: ExternalTechnician[]; total: number }>> =>
    api.get('/external-technicians', { params }),
  create: (data: Partial<ExternalTechnician> & { name: string }): Promise<AxiosResponse<{ data: ExternalTechnician }>> =>
    api.post('/external-technicians', data),
  update: (id: number, data: Partial<ExternalTechnician>): Promise<AxiosResponse<{ data: ExternalTechnician }>> =>
    api.put(`/external-technicians/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/external-technicians/${id}`),
};

// Sub-client group service
export const subClientGroupService = {
  getAll: (): Promise<AxiosResponse<{ data: SubClientGroup[] }>> =>
    api.get('/sub-client-groups'),
  getById: (id: number): Promise<AxiosResponse<{ data: SubClientGroup & { sub_clients?: SubClient[] } }>> =>
    api.get(`/sub-client-groups/${id}`),
  create: (data: Partial<SubClientGroup> & { sub_client_ids?: number[] }): Promise<AxiosResponse<{ data: SubClientGroup; message?: string }>> =>
    api.post('/sub-client-groups', data),
  update: (id: number, data: Partial<SubClientGroup> & { sub_client_ids?: number[] }): Promise<AxiosResponse<{ data: SubClientGroup; message?: string }>> =>
    api.put(`/sub-client-groups/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/sub-client-groups/${id}`),
};

// Sub-client service
export const subClientService = {
  getAll: (params?: { sub_client_group_id?: number; search?: string }): Promise<AxiosResponse<{ data: SubClient[] }>> =>
    api.get('/sub-clients', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: SubClient }>> => 
    api.get(`/sub-clients/${id}`),
  create: (data: Partial<SubClient>): Promise<AxiosResponse<{ data: SubClient; message: string }>> => 
    api.post('/sub-clients', data),
  update: (id: number, data: Partial<SubClient>): Promise<AxiosResponse<{ data: SubClient; message: string }>> => 
    api.put(`/sub-clients/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/sub-clients/${id}`),
  uploadDocument: (subClientId: number, documentFile: File, documentType: string, description?: string): Promise<AxiosResponse<{ data: any; message: string }>> => {
    const formData = new FormData();
    formData.append('document', documentFile);
    formData.append('document_type', documentType);
    if (description) formData.append('description', description);
    return api.post(`/sub-clients/${subClientId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getDocuments: (subClientId: number): Promise<AxiosResponse<{ data: any[]; total: number; verified_count: number; unverified_count: number }>> => 
    api.get(`/sub-clients/${subClientId}/documents`),
  deleteDocument: (subClientId: number, documentId: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/sub-clients/${subClientId}/documents/${documentId}`),
  getDocumentTypes: (): Promise<AxiosResponse<{ data: Record<string, string> }>> => 
    api.get('/sub-clients/document-types'),
};

// Vehicle settings service
export const vehicleSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/vehicle-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/vehicle-settings/types', data),
  createCategory: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/vehicle-settings/categories', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/vehicle-settings/types/${id}`, data),
  updateCategory: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/vehicle-settings/categories/${id}`, data),
  /** POST+_method=PUT so PHP receives the file (PUT does not populate $_FILES). */
  updateCategoryWithImage: (id: number, formData: FormData): Promise<AxiosResponse<{ data: any }>> => {
    formData.append('_method', 'PUT');
    return api.post(`/vehicle-settings/categories/${id}`, formData);
  },
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicle-settings/types/${id}`),
  deleteCategory: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicle-settings/categories/${id}`),
};

// Maintenance settings service
export const maintenanceSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/maintenance-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/maintenance-settings/types', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/maintenance-settings/types/${id}`, data),
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/maintenance-settings/types/${id}`),
};

// Technical note settings service
export const technicalNoteSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/technical-note-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/technical-note-settings/types', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/technical-note-settings/types/${id}`, data),
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/technical-note-settings/types/${id}`),
};

// Intervention settings service
export const interventionSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/intervention-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/intervention-settings/types', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/intervention-settings/types/${id}`, data),
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/intervention-settings/types/${id}`),
};

// Vehicle notes service
export const vehicleNotesService = {
  getNotes: (vehicleId: number): Promise<AxiosResponse<{ data: any[] }>> => 
    api.get(`/vehicles/${vehicleId}/notes`),
  createNote: (vehicleId: number, content: string): Promise<AxiosResponse<{ data: any }>> => 
    api.post(`/vehicles/${vehicleId}/notes`, { content }),
  deleteNote: (vehicleId: number, noteId: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/vehicles/${vehicleId}/notes/${noteId}`),
};

// Field Agent Activity Service
export interface AgentActivity {
  id: number;
  user_id: number;
  vehicle_id: number | null;
  action_type: 'scan' | 'status_update' | 'parking_assign' | 'parking_release' | 'note_add';
  old_value: string | null;
  new_value: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  user?: any;
  vehicle?: Vehicle;
}

export interface ActivityStatistics {
  total_scans: number;
  total_status_updates: number;
  total_parking_assigns: number;
  total_notes: number;
  today_activities: number;
  this_week_activities: number;
}

export interface ActivityFilters {
  action_type?: string;
  vehicle_id?: number;
  date_from?: string;
  date_to?: string;
  per_page?: number;
}

export const activityService = {
  getActivities: (params?: ActivityFilters): Promise<AxiosResponse<PaginatedResponse<AgentActivity>>> => 
    api.get('/agent/activities', { params }),
  logScan: (vehicleId: number, plateNumber: string): Promise<AxiosResponse<{ data: AgentActivity; message: string }>> => 
    api.post('/agent/activities/scan', { vehicle_id: vehicleId, plate_number: plateNumber }),
  getStatistics: (): Promise<AxiosResponse<ActivityStatistics>> => 
    api.get('/agent/activities/statistics'),
};

// Vehicle Condition Assessment Service (État des Lieux)
export const vehicleConditionAssessmentService = {
  // Get all assessments (for back office listing)
  getAllAssessments: (params?: { vehicle_id?: number; assessment_type?: string; status?: string; date_from?: string; date_to?: string; per_page?: number; page?: number }): Promise<AxiosResponse<any>> =>
    api.get('/condition-assessments', { params }),
  
  // Get all assessments for a vehicle
  getAssessments: (vehicleId: number): Promise<AxiosResponse<VehicleConditionAssessmentListResponse>> =>
    api.get(`/vehicles/${vehicleId}/condition-assessments`),

  // Create a new assessment
  createAssessment: (vehicleId: number, data: VehicleConditionAssessmentFormData | FormData): Promise<AxiosResponse<VehicleConditionAssessmentResponse>> =>
    api.post(`/vehicles/${vehicleId}/condition-assessments`, data),

  // Get a specific assessment
  getAssessment: (vehicleId: number, assessmentId: number): Promise<AxiosResponse<VehicleConditionAssessmentResponse>> =>
    api.get(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}`),

  // Update an assessment
  updateAssessment: (vehicleId: number, assessmentId: number, data: Partial<VehicleConditionAssessmentFormData>): Promise<AxiosResponse<VehicleConditionAssessmentResponse>> =>
    api.put(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}`, data),

  // Delete an assessment
  deleteAssessment: (vehicleId: number, assessmentId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}`),

  // Get assessment history grouped by date
  getAssessmentHistory: (vehicleId: number): Promise<AxiosResponse<VehicleConditionAssessmentHistoryResponse>> =>
    api.get(`/vehicles/${vehicleId}/condition-assessments/history`),

  // Link assessment to a repair/maintenance record
  linkRepair: (vehicleId: number, assessmentId: number, maintenanceRecordId: number, linkType: 'before_repair' | 'after_repair' | 'suggested_after_repair', notes?: string): Promise<AxiosResponse<{ data: any; message: string }>> =>
    api.post(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/link-repair`, {
      maintenance_record_id: maintenanceRecordId,
      link_type: linkType,
      notes
    }),

  // Get assessment data after vehicle scan
  getAfterScan: (vehicleId: number): Promise<AxiosResponse<VehicleConditionAssessmentAfterScanResponse>> =>
    api.get(`/vehicles/${vehicleId}/condition-assessments/after-scan`),

  // Suggest assessment after repair
  suggestAfterRepair: (vehicleId: number, maintenanceRecordId: number): Promise<AxiosResponse<VehicleConditionAssessmentSuggestAfterRepairResponse>> =>
    api.post(`/vehicles/${vehicleId}/condition-assessments/suggest-after-repair`, {
      maintenance_record_id: maintenanceRecordId
    }),

  // Upload photos for an assessment
  uploadPhotos: (vehicleId: number, assessmentId: number, photos: File[]): Promise<AxiosResponse<{ data: any[]; message: string }>> => {
    const formData = new FormData();
    photos.forEach((photo, index) => {
      formData.append(`photos[${index}]`, photo);
    });
    return api.post(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Delete a photo
  deletePhoto: (vehicleId: number, assessmentId: number, photoId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/photos/${photoId}`),

  // Add a note to an assessment
  addNote: (vehicleId: number, assessmentId: number, noteType: 'general' | 'damage' | 'alert' | 'maintenance' | 'other', content: string): Promise<AxiosResponse<{ data: any; message: string }>> =>
    api.post(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/notes`, {
      note_type: noteType,
      content
    }),

  // Update a note
  updateNote: (vehicleId: number, assessmentId: number, noteId: number, content: string): Promise<AxiosResponse<{ data: any; message: string }>> =>
    api.put(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/notes/${noteId}`, {
      content
    }),

  // Delete a note
  deleteNote: (vehicleId: number, assessmentId: number, noteId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/vehicles/${vehicleId}/condition-assessments/${assessmentId}/notes/${noteId}`),
};

// Condition Note Types Service
export const conditionNoteTypeService = {
  // Get all condition note types
  getTypes: (): Promise<AxiosResponse<{ success: boolean; data: any[] }>> =>
    api.get('/condition-note-types'),

  // Create a new condition note type
  createType: (data: {
    name: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }): Promise<AxiosResponse<{ success: boolean; data: any; message: string }>> =>
    api.post('/condition-note-types', data),

  // Get a specific condition note type
  getType: (id: number): Promise<AxiosResponse<{ success: boolean; data: any }>> =>
    api.get(`/condition-note-types/${id}`),

  // Update a condition note type
  updateType: (id: number, data: {
    name?: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }): Promise<AxiosResponse<{ success: boolean; data: any; message: string }>> =>
    api.put(`/condition-note-types/${id}`, data),

  // Delete (deactivate) a condition note type
  deleteType: (id: number): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.delete(`/condition-note-types/${id}`),
};

// Condition Photo Types Service
export const conditionPhotoTypeService = {
  getTypes: (): Promise<AxiosResponse<{ success: boolean; data: any[] }>> =>
    api.get('/condition-photo-types'),

  createType: (data: {
    code: string;
    name: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }): Promise<AxiosResponse<{ success: boolean; data: any; message: string }>> =>
    api.post('/condition-photo-types', data),

  getType: (id: number): Promise<AxiosResponse<{ success: boolean; data: any }>> =>
    api.get(`/condition-photo-types/${id}`),

  updateType: (id: number, data: {
    code?: string;
    name?: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }): Promise<AxiosResponse<{ success: boolean; data: any; message: string }>> =>
    api.put(`/condition-photo-types/${id}`, data),

  deleteType: (id: number): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.delete(`/condition-photo-types/${id}`),
};

// Email template service
export const emailTemplateService = {
  // Get email template for current client (optionally filtered by assessment_type)
  getTemplate: (assessmentType?: 'depart' | 'retour'): Promise<AxiosResponse<any>> => {
    const params = assessmentType ? { assessment_type: assessmentType } : {};
    return api.get('/email-template', { params });
  },

  // Create or update email template
  saveTemplate: (formData: FormData): Promise<AxiosResponse<any>> =>
    api.post('/email-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Delete an image from template
  deleteImage: (type: 'header_image' | 'logo' | 'footer_logo', assessmentType?: 'depart' | 'retour'): Promise<AxiosResponse<any>> => {
    const params = assessmentType ? { assessment_type: assessmentType } : {};
    return api.delete(`/email-template/images/${type}`, { params });
  },
};

export const conditionNoteSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> => 
    api.get('/condition-note-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.post('/condition-note-settings/types', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> => 
    api.put(`/condition-note-settings/types/${id}`, data),
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/condition-note-settings/types/${id}`),
}; 

export const conditionPhotoSettingsService = {
  getSettings: (): Promise<AxiosResponse<{ data: any }>> =>
    api.get('/condition-photo-settings'),
  createType: (data: any): Promise<AxiosResponse<{ data: any }>> =>
    api.post('/condition-photo-settings/types', data),
  updateType: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> =>
    api.put(`/condition-photo-settings/types/${id}`, data),
  deleteType: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/condition-photo-settings/types/${id}`),
};

// Pricing services
export const pricingService = {
  // Plans
  getPlans: (): Promise<AxiosResponse<{ data: PricingPlan[]; total: number }>> =>
    api.get('/pricing-plans'),
  getPlan: (id: number): Promise<AxiosResponse<{ data: PricingPlan }>> =>
    api.get(`/pricing-plans/${id}`),
  createPlan: (data: Partial<PricingPlan>): Promise<AxiosResponse<{ data: PricingPlan; message: string }>> =>
    api.post('/pricing-plans', data),
  updatePlan: (id: number, data: Partial<PricingPlan>): Promise<AxiosResponse<{ data: PricingPlan; message: string }>> =>
    api.put(`/pricing-plans/${id}`, data),
  deletePlan: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/pricing-plans/${id}`),
  activatePlan: (id: number): Promise<AxiosResponse<{ data: PricingPlan; message: string }>> =>
    api.post(`/pricing-plans/${id}/activate`),
  deactivatePlan: (id: number): Promise<AxiosResponse<{ data: PricingPlan; message: string }>> =>
    api.post(`/pricing-plans/${id}/deactivate`),
  duplicatePlan: (id: number): Promise<AxiosResponse<{ data: PricingPlan; message: string }>> =>
    api.post(`/pricing-plans/${id}/duplicate`),

  // Tiers
  getTiers: (): Promise<AxiosResponse<{ data: PricingTier[]; total: number }>> =>
    api.get('/pricing-tiers'),
  createTier: (data: Partial<PricingTier>): Promise<AxiosResponse<{ data: PricingTier; message: string }>> =>
    api.post('/pricing-tiers', data),
  updateTier: (id: number, data: Partial<PricingTier>): Promise<AxiosResponse<{ data: PricingTier; message: string }>> =>
    api.put(`/pricing-tiers/${id}`, data),
  deleteTier: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/pricing-tiers/${id}`),
  reorderTiers: (tiers: Array<{ id: number; sort_order: number }>): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/pricing-tiers/reorder', { tiers }),

  // Packs
  getPacks: (params?: {
    plan_id?: number;
    category_id?: number;
    /** If true, API also returns packs with no vehicle_category (shared tariffs for all categories) */
    include_universal?: boolean;
  }): Promise<AxiosResponse<{ data: PricingPack[]; total: number }>> =>
    api.get('/pricing-packs', { params }),
  getPack: (id: number): Promise<AxiosResponse<{ data: PricingPack }>> =>
    api.get(`/pricing-packs/${id}`),
  createPack: (data: Partial<PricingPack>): Promise<AxiosResponse<{ data: PricingPack; message: string }>> =>
    api.post('/pricing-packs', data),
  updatePack: (id: number, data: Partial<PricingPack>): Promise<AxiosResponse<{ data: PricingPack; message: string }>> =>
    api.put(`/pricing-packs/${id}`, data),
  deletePack: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/pricing-packs/${id}`),
  getPackPrestations: (id: number): Promise<AxiosResponse<{ data: any[] }>> =>
    api.get(`/pricing-packs/${id}/prestations`),
  syncPackPrestations: (id: number, prestationIds: number[]): Promise<AxiosResponse<{ data: PricingPack; message: string }>> =>
    api.post(`/pricing-packs/${id}/prestations`, { prestation_ids: prestationIds }),

  // Seasonal Rates
  getRates: (params?: { pack_id?: number; tier_id?: number; start_date?: string; end_date?: string }): Promise<AxiosResponse<{ data: SeasonalRate[]; total: number }>> =>
    api.get('/seasonal-rates', { params }),
  createRate: (data: Partial<SeasonalRate>): Promise<AxiosResponse<{ data: SeasonalRate; message: string }>> =>
    api.post('/seasonal-rates', data),
  updateRate: (id: number, data: Partial<SeasonalRate>): Promise<AxiosResponse<{ data: SeasonalRate; message: string }>> =>
    api.put(`/seasonal-rates/${id}`, data),
  deleteRate: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/seasonal-rates/${id}`),
  bulkCreateRates: (data: { pricing_pack_id: number; start_date: string; end_date: string; rates: Array<{ pricing_tier_id: number; price_ht: number; color?: string }> }): Promise<AxiosResponse<{ data: SeasonalRate[]; message: string }>> =>
    api.post('/seasonal-rates/bulk', data),
};


// Prestation services
export const prestationService = {
  getPrestations: (params?: { type?: 'OPTION' | 'ASSURANCE'; is_active?: boolean }): Promise<AxiosResponse<{ data: Prestation[]; total: number }>> =>
    api.get('/prestations', { params }),
  getPrestation: (id: number): Promise<AxiosResponse<{ data: Prestation }>> =>
    api.get(`/prestations/${id}`),
  createPrestation: (data: Partial<Prestation>): Promise<AxiosResponse<{ data: Prestation; message: string }>> =>
    api.post('/prestations', data),
  updatePrestation: (id: number, data: Partial<Prestation>): Promise<AxiosResponse<{ data: Prestation; message: string }>> =>
    api.put(`/prestations/${id}`, data),
  deletePrestation: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/prestations/${id}`),
  getPrestationRates: (id: number): Promise<AxiosResponse<{ data: CategoryPrestationRate[] }>> =>
    api.get(`/prestations/${id}/rates`),
  setCategoryRate: (id: number, data: { vehicle_category_id: number; pricing_tier_id: number; rate_ht: number }): Promise<AxiosResponse<{ data: CategoryPrestationRate; message: string }>> =>
    api.post(`/prestations/${id}/category-rate`, data),
  // Prestation Tier Rates (default rates per prestation/plan/tier - associé aux tarifs)
  getPrestationTierRates: (params?: { prestation_id?: number; pricing_plan_id?: number; tier_id?: number }): Promise<AxiosResponse<{ data: PrestationTierRate[]; total: number }>> =>
    api.get('/prestation-tier-rates', { params }),
  createPrestationTierRate: (data: Partial<PrestationTierRate>): Promise<AxiosResponse<{ data: PrestationTierRate; message: string }>> =>
    api.post('/prestation-tier-rates', data),
  updatePrestationTierRate: (id: number, data: Partial<PrestationTierRate>): Promise<AxiosResponse<{ data: PrestationTierRate; message: string }>> =>
    api.put(`/prestation-tier-rates/${id}`, data),
  deletePrestationTierRate: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/prestation-tier-rates/${id}`),
  getCategoryRates: (params?: { prestation_id?: number; category_id?: number; tier_id?: number }): Promise<AxiosResponse<{ data: CategoryPrestationRate[]; total: number }>> =>
    api.get('/category-prestation-rates', { params }),
  createCategoryRate: (data: Partial<CategoryPrestationRate>): Promise<AxiosResponse<{ data: CategoryPrestationRate; message: string }>> =>
    api.post('/category-prestation-rates', data),
  updateCategoryRate: (id: number, data: Partial<CategoryPrestationRate>): Promise<AxiosResponse<{ data: CategoryPrestationRate; message: string }>> =>
    api.put(`/category-prestation-rates/${id}`, data),
  deleteCategoryRate: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/category-prestation-rates/${id}`),
  getTypeRates: (params?: { prestation_id?: number; vehicle_type_id?: number; tier_id?: number }): Promise<AxiosResponse<{ data: TypePrestationRate[]; total: number }>> =>
    api.get('/type-prestation-rates', { params }),
  createTypeRate: (data: Partial<TypePrestationRate>): Promise<AxiosResponse<{ data: TypePrestationRate; message: string }>> =>
    api.post('/type-prestation-rates', data),
  updateTypeRate: (id: number, data: Partial<TypePrestationRate>): Promise<AxiosResponse<{ data: TypePrestationRate; message: string }>> =>
    api.put(`/type-prestation-rates/${id}`, data),
  deleteTypeRate: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/type-prestation-rates/${id}`),
};

// Agency service (pickup/return locations)
export const agencyService = {
  getAll: (): Promise<AxiosResponse<{ data: Agency[] }>> =>
    api.get('/agencies'),
  getById: (id: number): Promise<AxiosResponse<{ data: Agency }>> =>
    api.get(`/agencies/${id}`),
  create: (data: Partial<Agency>): Promise<AxiosResponse<{ data: Agency }>> =>
    api.post('/agencies', data),
  update: (id: number, data: Partial<Agency>): Promise<AxiosResponse<{ data: Agency }>> =>
    api.put(`/agencies/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/agencies/${id}`),
};

// Reservation service
export const reservationService = {
  getList: (params?: {
    search?: string;
    status?: string;
    date_depart_from?: string;
    date_depart_to?: string;
    date_retour_from?: string;
    date_retour_to?: string;
    sub_client_id?: number;
    include_cancelled?: boolean;
    per_page?: number;
    page?: number;
  }): Promise<AxiosResponse<{ data: Reservation[]; current_page: number; last_page: number; total: number; per_page: number }>> =>
    api.get('/reservations', { params }),
  getById: (id: number): Promise<AxiosResponse<{ data: Reservation }>> =>
    api.get(`/reservations/${id}`),
  create: (data: Partial<Reservation> & { prestations?: Array<{ prestation_id: number; name?: string; is_included?: boolean; pricing_mode?: string; unit_rate?: number; quantity?: number; total_amount?: number }> }): Promise<AxiosResponse<{ data: Reservation }>> =>
    api.post('/reservations', data),
  update: (id: number, data: Partial<Reservation>): Promise<AxiosResponse<{ data: Reservation }>> =>
    api.put(`/reservations/${id}`, data),
  cancel: (id: number): Promise<AxiosResponse<{ data: Reservation }>> =>
    api.delete(`/reservations/${id}`),
  getAvailability: (params: ReservationAvailabilityParams): Promise<AxiosResponse<{ data: ReservationAvailabilityResponse }>> =>
    api.get('/reservations/availability', { params }),
  checkPrestationStock: (data: {
    pickup_datetime: string;
    return_datetime: string;
    exclude_reservation_id?: number;
    prestations: Array<{ prestation_id: number; quantity: number }>;
  }): Promise<
    AxiosResponse<{
      data: {
        warnings: Array<{
          prestation_id: number;
          name: string;
          requested: number;
          available_for_period: number;
          stock_quantity: number;
          allocated_overlapping: number;
        }>;
      };
    }>
  > => api.post('/reservations/prestation-stock-check', data),
  getPlanning: (params: { start: string; end: string; vehicle_type_id?: number; transmission?: string; vehicle_id?: number; status?: string; search?: string }): Promise<AxiosResponse<{ data: { vehicles: Vehicle[]; reservations: Reservation[] } }>> =>
    api.get('/reservations/planning', { params }),
  export: (params?: { search?: string; status?: string; date_depart_from?: string; date_depart_to?: string }): Promise<AxiosResponse<Blob>> =>
    api.get('/reservations/export', { params, responseType: 'blob', timeout: 120000 }),
  downloadPdf: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/reservations/${id}/pdf`, { responseType: 'blob' }),
  getPayments: (id: number): Promise<AxiosResponse<{ data: any[] }>> =>
    api.get(`/reservations/${id}/payments`),
  addPayment: (id: number, data: any): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/payments`, data),
  deletePayment: (reservationId: number, paymentId: number): Promise<AxiosResponse> =>
    api.delete(`/reservations/${reservationId}/payments/${paymentId}`),
  getComments: (id: number): Promise<AxiosResponse<{ data: any[] }>> =>
    api.get(`/reservations/${id}/comments`),
  addComment: (id: number, data: { content: string }): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/comments`, data),
  deleteComment: (reservationId: number, commentId: number): Promise<AxiosResponse> =>
    api.delete(`/reservations/${reservationId}/comments/${commentId}`),
  getMailings: (id: number): Promise<AxiosResponse<{ data: any[] }>> =>
    api.get(`/reservations/${id}/mailings`),
  sendBonDeCommande: (id: number): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/mailings/send-bon-de-commande`),
  sendContract: (id: number): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/mailings/send-contract`),
  sendRecap: (id: number): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/mailings/send-recap`),
  sendRelance: (
    id: number,
    data: { subject: string; body: string; sent_to?: string }
  ): Promise<AxiosResponse<{ data: any }>> =>
    api.post(`/reservations/${id}/mailings/send-relance`, data),
};


export interface StopSellEntry {
  id: number;
  client_id: number;
  id_cat: number | null;
  id_veh: number | null;
  date_debut: string;
  date_fin: string | null;
  seuil: number | null;
  maintenance: boolean;
  note: string | null;
  date_crea: string | null;
  category?: { id: number; name: string } | null;
  vehicle?: { id: number; registration_number: string; marque: string; modele: string; vehicle_category_id: number } | null;
}

export const stopSellService = {
  getAll: (): Promise<AxiosResponse<{ data: StopSellEntry[] }>> =>
    api.get('/stop-sells'),
  create: (data: {
    id_cat?: number | null;
    id_veh?: number | null;
    date_debut: string;
    date_fin?: string | null;
    seuil?: number | null;
    note?: string | null;
  }): Promise<AxiosResponse<{ data: StopSellEntry; message: string }>> =>
    api.post('/stop-sells', data),
  delete: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/stop-sells/${id}`),
};

export const prestationResolveService = {
  resolveRates: (params: { pricing_plan_id: number; vehicle_category_id: number; rental_days: number; prestation_ids?: number[] }): Promise<AxiosResponse<{ data: Array<{ prestation_id: number; rate_ht: number | null; source: string | null }> }>> =>
    api.get('/prestations/resolve-rates', { params }),
};